import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatINR } from "../lib/api";
import { useSEO } from "../lib/seo";
import { Package, Search, Truck, XCircle } from "lucide-react";

const STEPS = ["order_placed", "confirmed", "packed", "shipped", "out_for_delivery", "delivered"];

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ order_number: params.get("order_number") || "", phone: "" });
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useSEO({ title: "Track Order — Haven Hives", description: "Track your Haven Hives order with your order number and mobile number." });

  const submit = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try { setOrder(await api.post("/orders/track", form).then((r) => r.data)); }
    catch (err) { setError(err.response?.data?.detail || "We couldn't find that order."); setOrder(null); }
    setBusy(false);
  };

  const cancel = async () => {
    try { await api.post(`/orders/${order.order_number}/cancel`, { phone: form.phone }); submit2(); } catch (e) { setError(e.response?.data?.detail); }
  };
  const submit2 = async () => {
    try { setOrder(await api.post("/orders/track", form).then((r) => r.data)); } catch { /* handled */ }
  };
  const requestReturn = async () => {
    try { await api.post(`/orders/${order.order_number}/return`, { phone: form.phone, reason: "Requested from Track Order page" }); submit2(); } catch (e) { setError(e.response?.data?.detail); }
  };

  const currentStep = order ? STEPS.indexOf(order.status) : -1;

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-12" data-testid="track-order-page">
      <h1 className="font-display font-semibold text-3xl sm:text-4xl">Track your order</h1>
      <p className="text-charcoal/70 mt-2 text-sm">Enter your order number and the mobile number you used at checkout. We protect order details with this two-step check.</p>

      <form onSubmit={submit} className="mt-8 bg-white rounded-3xl card-outline p-5 grid sm:grid-cols-2 gap-4" data-testid="track-order-form">
        <div>
          <label htmlFor="t-order" className="text-xs font-bold uppercase tracking-widest text-charcoal/60">Order number</label>
          <input id="t-order" required value={form.order_number} data-testid="track-order-number-input"
            onChange={(e) => setForm((f) => ({ ...f, order_number: e.target.value.toUpperCase() }))}
            placeholder="HH…" className="mt-1.5 w-full border border-charcoal/15 rounded-2xl px-4 py-3 text-sm" />
        </div>
        <div>
          <label htmlFor="t-phone" className="text-xs font-bold uppercase tracking-widest text-charcoal/60">Mobile number</label>
          <input id="t-phone" required inputMode="numeric" value={form.phone} data-testid="track-phone-input"
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
            placeholder="10-digit mobile" className="mt-1.5 w-full border border-charcoal/15 rounded-2xl px-4 py-3 text-sm" />
        </div>
        {error && <p role="alert" className="sm:col-span-2 text-sm text-coral font-semibold" data-testid="track-error">{error}</p>}
        <button type="submit" disabled={busy} className="sm:col-span-2 bg-forest text-white hover:bg-forest-dark py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-60" data-testid="track-submit">
          <Search className="w-4 h-4" /> {busy ? "Looking…" : "Find my order"}
        </button>
      </form>

      {order && (
        <div className="mt-8 bg-white rounded-3xl card-outline p-6" data-testid="track-result">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-display font-semibold text-xl">{order.order_number}</h2>
              <p className="text-xs text-charcoal/60 mt-0.5">{order.lines.length} items · {formatINR(order.total)} · {order.payment_method === "cod" ? "Cash on delivery" : "Prepaid"}</p>
            </div>
            <span className="sticker bg-butter rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wide" data-testid="track-status">{order.status.replace(/_/g, " ")}</span>
          </div>

          {["cancelled", "expired"].includes(order.status) ? (
            <div className="mt-5 bg-blush/40 rounded-2xl p-4 text-sm flex gap-2" data-testid="track-cancelled-state">
              <XCircle className="w-4 h-4 text-coral mt-0.5 shrink-0" />
              <p>This order was {order.status === "cancelled" ? "cancelled" : "not completed"} — no payment was taken{order.payment_status === "paid" ? ", and any charge will be refunded" : ""}.</p>
            </div>
          ) : (
            <ol className="mt-6 space-y-0" data-testid="track-timeline">
              {STEPS.map((s, i) => {
                const done = currentStep >= i && currentStep !== -1;
                const event = order.timeline?.find((t) => t.status === s);
                return (
                  <li key={s} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <span className={`w-3.5 h-3.5 rounded-full border-2 ${done ? "bg-forest border-forest" : "border-charcoal/25"}`} />
                      {i < STEPS.length - 1 && <span className={`w-0.5 h-8 ${currentStep > i ? "bg-forest" : "bg-charcoal/15"}`} />}
                    </div>
                    <div className="pb-2 -mt-1">
                      <p className={`text-sm font-semibold capitalize ${done ? "" : "text-charcoal/40"}`}>{s.replace(/_/g, " ")}</p>
                      {event?.at && <p className="text-[11px] text-charcoal/50">{new Date(event.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</p>}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}

          {order.awb && (
            <div className="text-sm bg-sage/30 rounded-2xl p-4 mt-2" data-testid="track-awb">
              <p><Truck className="w-4 h-4 inline mr-1.5 text-forest" /> Courier: {order.courier || "assigned by shipping partner"} · AWB {order.awb}</p>
              {order.tracking && (
                <div className="mt-3 pt-3 border-t border-forest/15" data-testid="track-live">
                  <p className="text-xs font-bold uppercase tracking-widest text-forest/80">Live courier updates{order.tracking.edd ? ` · expected ${order.tracking.edd}` : ""}</p>
                  {order.tracking.current_status && <p className="font-semibold mt-1">{order.tracking.current_status}</p>}
                  {order.tracking.activities?.length > 0 ? (
                    <ul className="mt-2 space-y-1.5">
                      {order.tracking.activities.map((a, i) => (
                        <li key={i} className="text-xs text-charcoal/75 flex gap-2">
                          <span className="shrink-0 text-charcoal/50 w-28">{a.at}</span>
                          <span>{a.activity || a.status}{a.location ? ` — ${a.location}` : ""}</span>
                        </li>
                      ))}
                    </ul>
                  ) : <p className="text-xs text-charcoal/60 mt-1">Awaiting the courier's first scan.</p>}
                </div>
              )}
            </div>
          )}
          {!order.awb && ["confirmed", "paid"].includes(order.status) && <p className="text-xs text-charcoal/50 mt-3">A courier is assigned when your parcel is packed. We'll show tracking here and email you at every step.</p>}

          <ul className="mt-5 pt-4 border-t border-charcoal/10 divide-y divide-charcoal/10 text-sm">
            {order.lines.map((l, i) => (
              <li key={i} className="flex justify-between py-2"><span>{l.name} ({l.variant_name}) × {l.qty}</span><span className="font-semibold">{formatINR(l.price * l.qty)}</span></li>
            ))}
          </ul>

          <div className="mt-5 flex gap-3 flex-wrap">
            {["confirmed", "pending_payment"].includes(order.status) && (
              <button onClick={cancel} data-testid="track-cancel-order" className="border border-coral text-coral font-semibold text-sm px-5 py-2.5 rounded-full">Cancel this order</button>
            )}
            {order.status === "delivered" && !order.return_requested && (
              <button onClick={requestReturn} data-testid="track-return-request" className="border border-charcoal/20 font-semibold text-sm px-5 py-2.5 rounded-full">Request a return</button>
            )}
            {order.return_requested && <span className="text-xs font-semibold text-forest self-center" data-testid="return-requested-note">Return requested — we'll email you the next steps.</span>}
          </div>
        </div>
      )}

      <p className="text-xs text-charcoal/50 mt-6 flex items-center gap-1.5"><Package className="w-3.5 h-3.5" /> Looking for all your orders? Visit <Link to="/account" className="underline font-semibold">My Orders</Link>.</p>
    </div>
  );
}
